class BlankApparelFinder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.state = { garment: 'all', brand: 'all', query: '', visible: 12 }
    this.results = this.querySelector('[data-results]')
    this.resultsStatus = this.querySelector('[data-results-status]')
    this.showMoreButton = this.querySelector('[data-show-more]')
    this.resetButton = this.querySelector('[data-reset]')
    this.loadProducts().then(() => this.start()).catch(() => {
      this.loadWarning = true
      this.products = JSON.parse(this.querySelector('[data-products]').textContent)
      this.start()
    })
  }

  start() {
    this.bindControls()
    this.updateOptionCounts()
    this.render()
  }

  async loadProducts() {
    const source = this.querySelector('[data-products]')
    this.products = JSON.parse(source.textContent)
    const currentPage = Number(source.dataset.currentPage || 1)
    const totalPages = Number(source.dataset.totalPages || 1)
    const collectionUrl = source.dataset.collectionUrl
    if (totalPages <= 1 || !collectionUrl) return

    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1).filter(page => page !== currentPage)
    const pageResults = await Promise.allSettled(pageNumbers.map(async page => {
      const response = await fetch(`${collectionUrl}?page=${page}&section_id=${encodeURIComponent(this.dataset.sectionId)}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      if (!response.ok) throw new Error(`Finder page ${page} failed`)
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html')
      const data = documentFragment.querySelector('[data-products]')
      if (!data) throw new Error(`Finder page ${page} has no data`)
      return JSON.parse(data.textContent)
    }))

    const pageProducts = pageResults.filter(result => result.status === 'fulfilled').map(result => result.value)
    this.loadWarning = pageResults.some(result => result.status === 'rejected')
    const merged = [...this.products, ...pageProducts.flat()]
    this.products = [...new Map(merged.map(product => [product.id, product])).values()]
  }

  bindControls() {
    this.querySelectorAll('[data-filter-group]').forEach(group => {
      group.querySelectorAll('[data-filter-value]').forEach(button => {
        button.addEventListener('click', () => {
          const key = group.dataset.filterGroup
          this.state[key] = button.dataset.filterValue
          this.state.visible = 12
          group.querySelectorAll('[data-filter-value]').forEach(option => option.setAttribute('aria-pressed', String(option === button)))
          this.render()
          this.track('filter', { filter: key, value: this.state[key], resultCount: this.filteredProducts().length })
        })
      })
    })

    const search = this.querySelector('[data-search]')
    search.addEventListener('input', () => {
      this.state.query = search.value.trim().toLowerCase()
      this.state.visible = 12
      this.render()
    })
    search.addEventListener('change', () => this.track('filter', { filter: 'search', value: this.state.query, resultCount: this.filteredProducts().length }))

    const brand = this.querySelector('[data-brand]')
    brand.addEventListener('change', () => {
      this.state.brand = brand.value
      this.state.visible = 12
      this.render()
      this.track('filter', { filter: 'brand', value: this.state.brand, resultCount: this.filteredProducts().length })
    })

    this.resetButton.addEventListener('click', () => this.resetFilters())
    this.showMoreButton.addEventListener('click', () => {
      this.state.visible += 12
      this.render()
    })
  }

  resetFilters() {
    this.state = { garment: 'all', brand: 'all', query: '', visible: 12 }
    this.querySelector('[data-search]').value = ''
    this.querySelector('[data-brand]').value = 'all'
    this.querySelectorAll('[data-filter-group]').forEach(group => {
      group.querySelectorAll('[data-filter-value]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filterValue === 'all')))
    })
    this.render()
    this.track('filter_reset', { resultCount: this.products.length })
  }

  matchesGarment(product, value = this.state.garment) {
    return value === 'all' || product.garment === value
  }

  filteredProducts() {
    const filtered = this.products.filter(product => {
      const searchable = `${product.title} ${product.vendor}`.toLowerCase()
      return this.matchesGarment(product) && (this.state.brand === 'all' || product.vendor === this.state.brand) && (!this.state.query || searchable.includes(this.state.query))
    })

    return filtered.sort((a, b) => a.catalogIndex - b.catalogIndex)
  }

  updateOptionCounts() {
    this.querySelectorAll('[data-filter-group]').forEach(group => {
      group.querySelectorAll('[data-filter-value]').forEach(button => {
        const value = button.dataset.filterValue
        const count = this.products.filter(product => this.matchesGarment(product, value)).length
        const countLabel = button.querySelector('[data-option-count]')
        if (countLabel) countLabel.textContent = count
        button.disabled = count === 0
      })
    })
  }

  render() {
    const products = this.filteredProducts()
    this.results.replaceChildren()
    products.slice(0, this.state.visible).forEach(product => this.results.append(this.productCard(product)))

    if (!products.length) {
      const empty = this.element('div', 'ba-finder-empty')
      empty.append(this.element('h3', '', 'Nothing matched that search.'))
      empty.append(this.element('p', '', 'Try another brand, product code, or category.'))
      this.results.append(empty)
    }

    const warning = this.loadWarning ? '; some catalog pages could not load' : ''
    this.resultsStatus.textContent = `${products.length} ${products.length === 1 ? 'product' : 'products'}${warning}`
    this.showMoreButton.hidden = products.length <= this.state.visible
    this.resetButton.hidden = this.state.garment === 'all' && this.state.brand === 'all' && !this.state.query
  }

  productCard(product) {
    const card = this.element('article', 'ba-finder-card')
    const media = this.element('div', 'ba-finder-card-media')
    if (product.image) {
      const mediaLink = this.productLink(product, '', 'product_click', 'ba-finder-media-link')
      mediaLink.setAttribute('aria-label', `Shop ${product.title}`)
      const image = document.createElement('img')
      image.src = product.image
      image.alt = product.title
      image.loading = 'lazy'
      image.width = 360
      image.height = 360
      mediaLink.append(image)
      media.append(mediaLink)
    }

    const body = this.element('div', 'ba-finder-card-body')
    const vendor = this.element('p', 'ba-finder-card-vendor', product.vendor)

    const heading = this.element('h3')
    const titleLink = this.productLink(product, product.title, 'product_click')
    heading.append(titleLink)

    const foot = this.element('div', 'ba-finder-card-foot')
    const price = this.element('div', 'ba-finder-price')
    price.append(this.element('strong', '', product.priceLabel))
    foot.append(price)
    foot.append(this.productLink(product, 'Choose colors & sizes', 'product_click', 'ba-finder-product-link'))

    body.append(vendor, heading, foot)
    card.append(media, body)
    return card
  }

  productLink(product, label, eventName, className = '') {
    const link = this.element('a', className, label)
    link.href = product.url
    link.addEventListener('click', () => this.track(eventName, { productId: product.id, productTitle: product.title }))
    return link
  }

  element(tag, className = '', text = '') {
    const element = document.createElement(tag)
    if (className) element.className = className
    if (text) element.textContent = text
    return element
  }

  track(action, detail) {
    const payload = { action, sectionId: this.dataset.sectionId, ...detail }
    this.dispatchEvent(new CustomEvent('dtfsheet:blank-finder', { bubbles: true, detail: payload }))
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `blank_finder_${action}`, ...payload })
  }
}

if (!customElements.get('blank-apparel-finder')) customElements.define('blank-apparel-finder', BlankApparelFinder)
